
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/components/AuthProvider";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export const useCalendarEvents = (date: Date, view: string) => {
  const { user } = useAuth();
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchEvents = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);

    try {
      // Buscar eventos normais
      const { data: scheduledEvents, error: eventsError } = await supabase
        .from('schedule_events')
        .select(`
          *,
          students:student_id (
            first_name,
            last_name
          )
        `)
        .eq('trainer_id', user.id);

      if (eventsError) {
        console.error('Error fetching scheduled events:', eventsError);
        throw eventsError;
      }

      // Buscar pedidos de aulas (pendentes e aprovados)
      const { data: requests, error: requestsError } = await supabase
        .from('schedule_requests')
        .select(`
          id,
          trainer_id,
          student_id,
          requested_date,
          status,
          student:students!schedule_requests_student_id_fkey (
            first_name,
            last_name
          )
        `)
        .eq('trainer_id', user.id)
        .in('status', ['pending', 'approved']);

      if (requestsError) {
        console.error('Error fetching requests:', requestsError);
        throw requestsError;
      }

      // Converter pedidos em eventos
      const requestEvents = requests?.map(request => ({
        id: request.id,
        trainer_id: request.trainer_id,
        student_id: request.student_id,
        title: `Pedido de Aula`,
        start_time: request.requested_date,
        end_time: new Date(new Date(request.requested_date).getTime() + 60 * 60 * 1000),
        status: request.status,
        students: request.student,
        event_type: 'class_request'
      })) || [];

      const allEvents = [...(scheduledEvents || []), ...requestEvents];
      console.log('Eventos carregados (Personal):', allEvents);
      setEvents(allEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Function to update the events list when an event is cancelled or deleted
  const removeEventFromState = (eventId: string) => {
    setEvents(current => current.filter(event => event.id !== eventId));
  };

  // Add a temporary event (for immediate UI feedback)
  const addTemporaryEvent = (newEvent: any) => {
    setEvents(current => [...current, newEvent]);
  };

  useEffect(() => {
    fetchEvents();

    // Configurar subscription para atualizações em tempo real
    const channel = supabase
      .channel('calendar-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'schedule_requests'
        },
        (payload) => {
          console.log('Schedule request changed, payload:', payload);
          
          // Handle deletion or status change to cancelled/rejected
          if (payload.eventType === 'DELETE' || 
              (payload.new && typeof payload.new === 'object' && 
               'status' in payload.new && 
               (payload.new.status === 'cancelled' || payload.new.status === 'rejected'))) {
            if (payload.old && typeof payload.old === 'object' && 'id' in payload.old) {
              removeEventFromState(payload.old.id as string);
            }
          }
          
          // If the request status changes to approved, we need a full fetch to get the new event
          if (payload.new && typeof payload.new === 'object' && 
              'status' in payload.new && payload.new.status === 'approved') {
            fetchEvents();
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'schedule_events'
        },
        (payload) => {
          console.log('Schedule event changed, payload:', payload);
          
          // If the event was updated to cancelled status or deleted, remove it from the UI immediately
          if (payload.eventType === 'DELETE' ||
              (payload.new && typeof payload.new === 'object' && 
              'status' in payload.new && payload.new.status === 'cancelled')) {
            if (payload.old && typeof payload.old === 'object' && 'id' in payload.old) {
              removeEventFromState(payload.old.id as string);
            }
          }
          
          // For new events (INSERT), add them immediately to the UI
          if (payload.eventType === 'INSERT' && payload.new) {
            fetchEvents();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchEvents]);

  return { 
    events, 
    loading, 
    fetchEvents,
    removeEventFromState,
    addTemporaryEvent
  };
};
